import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { notify } from "components/ui";
import { getSuggestedMaskingRules } from "services/maskingService";
import { suggestionService } from "services/suggestionService";
import {
  SUGGESTION_BANNER_KEYS,
  SuggestionBannerKey,
  suggestionBannerService,
} from "services/suggestionBannerService";

interface SuggestionsContextType {
  /** Masking rule suggestions awaiting a decision. Excludes dismissed ones. */
  maskingPendingCount: number;
  /** Widget shortcut suggestions awaiting a decision. Excludes dismissed and accepted ones. */
  shortcutPendingCount: number;
  /** True when this user has closed the masking banner and no new suggestion has reset it. */
  maskingBannerDismissed: boolean;
  /** True when this user has closed the shortcut banner and no new suggestion has reset it. */
  shortcutBannerDismissed: boolean;
  /**
   * False until the first load resolves. Banners must wait for this — rendering before the dismissal
   * state is known makes an already-dismissed banner flash on screen.
   */
  initialized: boolean;
  /** Hides a banner for this user only. Persisted server-side, so it survives logout. */
  dismissBanner: (bannerKey: SuggestionBannerKey) => void;
  refreshCounts: () => void;
}

const SuggestionsContext = createContext<SuggestionsContextType>({
  maskingPendingCount: 0,
  shortcutPendingCount: 0,
  maskingBannerDismissed: false,
  shortcutBannerDismissed: false,
  initialized: false,
  dismissBanner: () => {},
  refreshCounts: () => {},
});

/**
 * Pending counts and per-user banner dismissal for every kind of AI-generated suggestion, shared by the
 * topbar banners and the Suggestions page.
 *
 * Dismissal lives on the server (User.DismissedBannersJson), not in component state or localStorage, for
 * two reasons: it has to survive logging out, and the analytics job has to be able to clear it when new
 * suggestions appear so a dismissal never hides a banner permanently.
 */
export const SuggestionsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const [maskingPendingCount, setMaskingPendingCount] = useState(0);
  const [shortcutPendingCount, setShortcutPendingCount] = useState(0);
  const [dismissed, setDismissed] = useState<SuggestionBannerKey[]>([]);
  const [initialized, setInitialized] = useState(false);

  const refreshCounts = useCallback(async () => {
    // Fetched CONCURRENTLY and applied together, not one after another. Awaiting them in sequence made
    // the counts land before the dismissal state, so an already-dismissed banner flashed on screen for
    // the length of the third request before correcting itself.
    //
    // All three are decoration — one failing must not blank the others, and none should surface an
    // error toast over whatever page the user is actually on. allSettled gives that for free.
    const [maskingResult, shortcutResult, dismissedResult] =
      await Promise.allSettled([
        getSuggestedMaskingRules(),
        suggestionService.pendingCount(),
        suggestionBannerService.listDismissed(),
      ]);

    if (maskingResult.status === "fulfilled") {
      setMaskingPendingCount(maskingResult.value.length);
    }
    if (shortcutResult.status === "fulfilled") {
      setShortcutPendingCount(shortcutResult.value);
    }
    if (dismissedResult.status === "fulfilled") {
      setDismissed(dismissedResult.value);
    }

    // Gate the banners until the first load resolves, so they can never render against a default
    // "nothing dismissed" state. Set last, and regardless of which calls failed — a failed dismissal
    // read means we show the banner, which is the safe direction.
    setInitialized(true);
  }, []);

  const dismissBanner = useCallback(
    async (bannerKey: SuggestionBannerKey) => {
      // Hide immediately; the write is confirmation, not permission.
      setDismissed((prev) =>
        prev.includes(bannerKey) ? prev : [...prev, bannerKey],
      );
      try {
        await suggestionBannerService.dismiss(bannerKey);
      } catch (error: any) {
        // Put it back so the next page load is honest about the real state.
        setDismissed((prev) => prev.filter((k) => k !== bannerKey));

        // Unlike the counts above, this is a WRITE the user initiated. Swallowing the failure makes a
        // broken save look identical to no request at all, which is exactly how this went unnoticed.
        notify({
          type: "error",
          title: t("app.suggestions.errors.bannerDismissFailed"),
          description: error?.response?.data?.message ?? error?.message,
        });
      }
    },
    [t],
  );

  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  return (
    <SuggestionsContext.Provider
      value={{
        maskingPendingCount,
        shortcutPendingCount,
        maskingBannerDismissed: dismissed.includes(
          SUGGESTION_BANNER_KEYS.masking,
        ),
        shortcutBannerDismissed: dismissed.includes(
          SUGGESTION_BANNER_KEYS.shortcuts,
        ),
        initialized,
        dismissBanner,
        refreshCounts,
      }}
    >
      {children}
    </SuggestionsContext.Provider>
  );
};

export const useSuggestions = () => useContext(SuggestionsContext);
